import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveStickyNotes, deleteStickyNotes } from '../../../apiServices/reducer/apiService';
import './StickyNotes.css';
import { Trash2 } from 'react-feather';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import Draggable from 'react-draggable';

const StickyNotes = ({ index, onClose, onAdd, onDelete, sticky, onUpdate, userId }) => {
    const dispatch = useDispatch();
    const [noteContent, setNoteContent] = useState('');
    const [updatedOn, setUpdatedOn] = useState('');
    const [showData, setShowData] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const token = useSelector((state) => state.apiService.token);
    const saveTimeoutRef = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const nodeRef = useRef(null);

    // Initialize note data when sticky prop changes
    useEffect(() => {
        if (sticky?.sticky_id > 0) {
            setNoteContent(sticky.notes || '');
            setUpdatedOn(sticky.LastUpdatedOn || '');
        } else {
            setNoteContent('');
            setUpdatedOn('');
        }
        setShowData(true);
    }, [sticky]);

    // Handle when content becomes empty
    useEffect(() => {
        if (!noteContent.trim()) {
            setUpdatedOn('');
        }
    }, [noteContent]);

    // Auto-save functionality with debounce
    const saveNote = useCallback(
        (content, pos = position) => {
            if (!userId || !token) {
                return;
            }

            // Clear any existing timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            // Set new timeout for auto-save
            saveTimeoutRef.current = setTimeout(async () => {
                const params = {
                    UserID: userId.toString(),
                    Notes: content,
                    stickyID: sticky?.sticky_id,
                    positionX: pos.x,
                    positionY: pos.y,
                    token: token
                };

                try {
                    const result = await dispatch(saveStickyNotes(params)).unwrap();
                    
                    if (result?.Table?.[0]) {
                        const updatedDate = result.Table[0].updated_on || '';
                        const newStickyId = result.Table[0].sticky_id || sticky?.sticky_id;
                        
                        // Only set updated time if there's content, otherwise clear it
                        if (content.trim()) {
                            setUpdatedOn(updatedDate);
                        } else {
                            setUpdatedOn('');
                        }
                        
                        // Update parent component with new data
                        if (onUpdate) {
                            onUpdate(index, {
                                sticky_id: newStickyId,
                                updated_on: content.trim() ? updatedDate : '',
                                notes: content,
                                user_id: userId
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error saving sticky note:', error);
                    if (error.message?.includes('401')) {
                        console.error('Authentication failed - please check your login status');
                    }
                }
            }, 1000); // 1 second debounce
        },
        [dispatch, userId, token, sticky?.sticky_id, position, onUpdate, index]
    );

    // Handle input changes with auto-save
    const handleInputChange = (e) => {
        const newContent = e.target.value;
        setNoteContent(newContent);
        
        // Clear the updated time if content is empty
        if (!newContent.trim()) {
            setUpdatedOn('');
        }
        
        // Always save, even if content is empty
        saveNote(newContent);
    };

    // Handle keydown events to better handle backspace
    const handleKeyDown = (e) => {
        // If backspace is pressed and content is about to be empty, clear any pending saves
        if (e.key === 'Backspace' && noteContent.length === 1) {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        }
    };

    // Handle delete with API call
    const handleDelete = () => {
        if (!userId || !token) {
            console.error("User not authenticated or no user selected");
            return;
        }
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            if (!userId || !token) {
                console.error("User not authenticated or no user selected");
                return;
            }

            const deleteData = {
                UserID: userId,
                stickyID: sticky?.sticky_id,
            };

            const result = await dispatch(deleteStickyNotes(deleteData)).unwrap();
            
            if (result) {
                onDelete(sticky?.sticky_id, index);
            }
        } catch (error) {
            console.error("Error deleting note:", error);
            if (error.response?.status === 401) {
                console.error("Authentication failed while deleting note");
            }
        } finally {
            setShowDeleteModal(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
    };

    useEffect(() => {
        if (sticky?.positionX !== undefined && sticky?.positionY !== undefined) {
            setPosition({ x: sticky.positionX, y: sticky.positionY });
        } else {
            setPosition({ x: 20, y: 70 }); // Default position
        }
    }, [sticky]);

    const handleDragStop = (e, data) => {
        const newPosition = { x: data.x, y: data.y };
        setPosition(newPosition);
        if (noteContent.trim()) {
            // Save the note with the new position
            saveNote(noteContent, newPosition);
        }
    };

    return (
        <>
          <Draggable
            position={position}
            onStop={handleDragStop}
            handle=".sticky-note-header"
            nodeRef={nodeRef}
        >
            <div className="sticky-notes-container" ref={nodeRef}>
                <div className="sticky-note-header">
                    <div>
                        <button className="add-button" title="New" onClick={onAdd}>+</button>
                    </div>
                    <div>
                        <h5 className='text-capitalize'>Notes - {index + 1}</h5>
                    </div>
                    <div>
                        <button className="close-button" onClick={() => onClose(sticky?.sticky_id)}>x</button>
                    </div>
                </div>
                {showData && (
                    <textarea
                        value={noteContent}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Take a note..."
                        className="sticky-note-textarea"
                    />
                )}
                <div className="Footer">
                    <button
                        type="button"
                        className="btn btn-icon text-left"
                        onClick={handleDelete}
                        title="Delete"
                    >
                        <Trash2 color="#d32f2f" size={22} />
                    </button>
                    {noteContent.trim() && updatedOn && (
                        <small
                            className="text-right mr-50 text-capitalize"
                            style={{ marginLeft: 'auto', display: 'block', width: '100%', textAlign: 'right' }}
                        >
                            Last Saved On: {new Date(updatedOn).toLocaleString('en-US', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            }).replace(',', '')}
                        </small>
                    )}
                </div>
            </div>
        </Draggable>
        <DeleteConfirmationModal
            show={showDeleteModal}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
            noteNumber={index + 1}
        />
        </>
      
        
    );
};

export default StickyNotes; 